'use client';

import { useEffect, useState } from 'react';
import Switch from '../switch/switch';
import { timeInput, inputStyle } from './time-input.css';
import { switchStyle } from '../switch/switch.css';

enum Period {
  AM = 'AM',
  PM = 'PM',
}

const canBecomeValid = (x: number) =>
  (x >= 0 && x <= 65) ||
  (x >= 70 && x <= 75) ||
  (x >= 80 && x <= 85) ||
  (x >= 90 && x <= 95) ||
  (x < 1000 && x % 100 < 60) ||
  (x >= 1000 && x <= 1059) ||
  (x >= 1100 && x <= 1159) ||
  (x >= 1200 && x <= 1259);

const intToTime = (x: number, period: Period) => {
  if (!Number.isInteger(x)) return 0;
  if (x < 12) return (period === Period.AM ? x : x + 12) * 60 * 60; // 0-11 becomes X:00
  if (x === 12 && period === Period.AM) return 0; // 12 becomes '12:00'
  if (x < 100) return (period === Period.AM ? x : x + 720) * 60; // 75 becomes 1:15
  if (x < 1200)
    return Math.floor(x / 100) * 60 * 60 + (x % 100) * 60 + (period === Period.PM ? 720 * 60 : 0);
  if (x === 1200) return period === Period.AM ? 0 : 720 * 60;
  return (x % 100) * 60 + (period === Period.PM ? 720 * 60 : 0); //  1215 becomes 00:15
};

const timeToString = (time?: number) => {
  if (typeof time !== 'number') return '';
  const hours = Math.floor(time / 3600) % 12;
  const minutes = Math.floor(time / 60) % 60;
  return (
    (hours === 0 ? '12' : (hours < 10 ? '0' : '') + hours) +
    ':' +
    ((minutes < 10 ? '0' : '') + minutes)
  );
};

const strToInt = (str: string) => parseInt(str.replace(/\D/g, ''));

const prettifyInput = (input: string) => {
  if (input.length === 4) return input.substring(0, 2) + ':' + input.substring(2, 4);
  if (input.length === 3) return input.substring(0, 1) + ':' + input.substring(1, 3);
  return input;
};

const timeToPeriod = (time?: number) => (time && time >= 12 * 60 * 60 ? Period.PM : Period.AM);

type TimeInputProps = {
  /** Time in seconds since midnight */
  time?: number;
  onChange?: (time: number) => void;
  variant?: keyof typeof switchStyle;
  disabled?: boolean;
};

export const TimeInput = ({
  time,
  variant,
  onChange,
  disabled = false,
  ...props
}: TimeInputProps) => {
  const [input, setInput] = useState({
    time: timeToString(time),
    period: timeToPeriod(time),
  });

  useEffect(() => {
    setInput((input) => {
      const inputTime = intToTime(strToInt(input.time), input.period);
      return time === inputTime ? input : { time: timeToString(time), period: timeToPeriod(time) };
    });
  }, [time]);

  const handleTimeChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    if (!e.target.value) return setInput({ time: '', period: input.period });
    const inputNum = strToInt(e.target.value);
    if (canBecomeValid(inputNum)) {
      setInput({ time: prettifyInput(String(inputNum)), period: input.period });
      console.log('intToTime(inputNum, input.period)', intToTime(inputNum, input.period));
      onChange?.(intToTime(inputNum, input.period));
    }
  };

  const handlePeriodChange = (value: Period) => {
    setInput({ time: input.time, period: value });
    onChange?.(intToTime(strToInt(input.time), value));
  };

  const handleTimeBlur: React.FocusEventHandler<HTMLInputElement> = (e) => {
    setInput({
      time: timeToString(intToTime(strToInt(e.target.value), input.period)),
      period: input.period,
    });
  };

  const handleFocus: React.FocusEventHandler<HTMLInputElement> = (e) =>
    setTimeout(() => e.target.select(), 0); // need to use setTimeout for Safari on OSX

  return (
    <div className={timeInput}>
      <input
        style={{
          width: variant === 'sm' ? '80px' : '',
          height: variant === 'sm' ? '52px' : '',
          fontSize: variant === 'sm' ? '0.9rem' : '16px',
        }}
        className={inputStyle}
        placeholder="00:00"
        type="tel"
        value={input.time}
        onChange={handleTimeChange}
        onBlur={handleTimeBlur}
        onFocus={handleFocus}
        disabled={disabled}
        {...props}
      />
      <Switch
        onChange={(on) => handlePeriodChange(on ? Period.AM : Period.PM)}
        labels={[Period.AM, Period.PM]}
        on={input.period === Period.AM}
        variant={variant}
        disabled={disabled}
      />
    </div>
  );
};
